import { useEffect, useState } from 'react';
import type { QuoteRecord } from '../types';
import { fetchQuoteByTicketNumber } from './api';
import { sanitizeEticketNumber } from './skyluxReceiptIds';
import { loadQuotes, saveQuote } from './skyluxStore';

function findQuoteInCache(ticketNumber: string): QuoteRecord | undefined {
  const target = sanitizeEticketNumber(ticketNumber);
  if (!target) return undefined;
  for (const q of loadQuotes()) {
    if (sanitizeEticketNumber(q.eTicketNumber) === target) return q;
    for (const p of q.passengers ?? []) {
      if (sanitizeEticketNumber(p.eTicketNumber) === target) return q;
    }
  }
  return undefined;
}

/** Load a quote for /ticket/:ticketNumber — server first, then browser cache. */
export function usePublicQuoteByTicket(ticketNumber: string | null) {
  const [quote, setQuote] = useState<QuoteRecord | undefined>(undefined);
  const [loading, setLoading] = useState(!!ticketNumber);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ticketNumber) {
      setQuote(undefined);
      setLoading(false);
      setError(null);
      return;
    }

    if (!sanitizeEticketNumber(ticketNumber)) {
      setQuote(undefined);
      setLoading(false);
      setError('Invalid ticket number. Use the 13-digit number from your e-ticket email.');
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    const cached = findQuoteInCache(ticketNumber);
    if (cached) setQuote(cached);

    void (async () => {
      try {
        const fromApi = await fetchQuoteByTicketNumber(ticketNumber);
        if (!active) return;
        setQuote(fromApi);
        saveQuote(fromApi);
        setError(null);
      } catch (e) {
        if (!active) return;
        if (cached) {
          setQuote(cached);
          setError(null);
        } else {
          setQuote(undefined);
          setError(e instanceof Error ? e.message : 'Ticket not found');
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [ticketNumber]);

  return { quote, loading, error };
}
